import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

export default function BookDetail() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get(`/books/${id}`).then(({ data }) => setBook(data)).catch(() => setBook(null));
  }, [id]);

  const handleBuy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/book", {
        book_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setBuying(false);
    }
  };

  if (!book) {
    return <div className="max-w-3xl mx-auto px-6 py-32" style={{ color: "var(--muted)" }}>Loading…</div>;
  }

  return (
    <div data-testid="book-detail-page" className="max-w-7xl mx-auto px-6 lg:px-12 py-8 md:py-16">
      <Link to="/books" className="inline-flex items-center gap-2 text-sm mb-10" style={{ color: "var(--ink-2)" }}>
        <ArrowLeft size={16} /> Back to all books
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="aspect-[2/3] overflow-hidden flex items-center justify-center" style={{ background: "var(--surface)" }}>
            <img src={book.cover_url} alt={book.title} className="w-full h-full object-contain" />
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="overline mb-4">{book.category}</div>
          <h1 className="font-serif-display text-4xl lg:text-6xl tracking-tight leading-[1.05]" style={{ color: "var(--ink)" }}>
            {book.title}
          </h1>
          {book.subtitle && (
            <p className="font-serif-display italic mt-3 text-xl lg:text-2xl" style={{ color: "var(--ink-2)" }}>
              {book.subtitle}
            </p>
          )}
          <div className="editorial-rule my-8" />
          <p className="text-base md:text-lg leading-relaxed whitespace-pre-line" style={{ color: "var(--ink-2)" }}>
            {book.description}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <span className="font-serif-display text-3xl" style={{ color: "var(--ink)" }}>${book.price.toFixed(2)}</span>
            <button
              onClick={handleBuy}
              disabled={buying}
              data-testid="book-buy-button"
              className="btn-primary disabled:opacity-50"
            >
              {buying ? "Redirecting…" : (<>Buy this book <ShoppingBag size={16} /></>)}
            </button>
            {book.sample_chapter && (
              <button
                onClick={() => document.getElementById("sample-chapter")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="book-read-sample-button"
                className="btn-ghost"
              >
                Read a free sample
              </button>
            )}
          </div>
          <p className="mt-6 text-xs" style={{ color: "var(--muted)" }}>
            Secure checkout via Stripe. Free shipping in the U.S. — international rates apply.
          </p>
        </div>
      </div>

      {book.sample_chapter && (
        <section id="sample-chapter" className="mt-24 pt-16 border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="max-w-3xl mx-auto">
            <div className="overline mb-4">Free sample</div>
            <h2 className="font-serif-display text-4xl lg:text-6xl tracking-[-0.03em] leading-[0.95]" style={{ color: "var(--ink)" }}>
              Read the first chapter.
            </h2>
            <div className="mt-10 text-lg leading-[1.8] whitespace-pre-line" style={{ color: "var(--ink-2)" }} data-testid="book-sample-content">
              {book.sample_chapter}
            </div>
            <div className="editorial-rule my-10" />
            <div className="text-center">
              <p className="mb-6" style={{ color: "var(--ink-2)" }}>Like what you're reading? Take the whole book home.</p>
              <button onClick={handleBuy} disabled={buying} className="btn-primary">
                Buy "{book.title}" — ${book.price.toFixed(2)} <ShoppingBag size={16} />
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
